import { useEffect, useRef, useState } from 'react';

// `app/24/TypingCanvas.js`. Splits the source file into lines that fit the container by
// measuring a hidden probe span, then reveals one line at a time — each line animating
// its own width from 0 — and stops as soon as the next line would fall below the fold.
// Kept measurement-for-measurement identical to the original; the class names come from
// stylesheets/hack24.css instead of the CSS module.
export default function TypingCanvas({
  code,
  speed = 50,
}: {
  code: string;
  speed?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [lines, setLines] = useState<string[]>([]);
  const [visibleLines, setVisibleLines] = useState(0);
  const visibleHeightRef = useRef(0);
  const lineHeightRef = useRef(0);

  useEffect(() => {
    const splitTextIntoLines = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.offsetWidth;
      const tempElement = document.createElement('span');
      tempElement.style.font = getComputedStyle(container).font;
      tempElement.style.visibility = 'hidden';
      tempElement.style.position = 'absolute';
      tempElement.textContent = 'M'; // Use a capital M to measure line height
      document.body.appendChild(tempElement);

      // Calculate line height based on font size
      const fontSize = Number.parseFloat(getComputedStyle(tempElement).fontSize);
      lineHeightRef.current = Math.ceil(fontSize * 1.2);

      const words = code.split(/\s+/);
      const newLines: string[] = [];
      let currentLine = '';

      for (const word of words) {
        tempElement.textContent = `${currentLine} ${word}`;
        if (tempElement.offsetWidth > containerWidth - 20) {
          if (currentLine) {
            newLines.push(currentLine.trim());
            currentLine = word;
          } else {
            // Split the word if it's too long for a single line
            const halfLength = Math.ceil(word.length / 2);
            newLines.push(word.slice(0, halfLength));
            currentLine = word.slice(halfLength);
          }
        } else {
          currentLine += (currentLine ? ' ' : '') + word;
        }
      }

      if (currentLine) {
        newLines.push(currentLine.trim());
      }

      // Check if the last line is too long and split if necessary
      const lastLine = newLines[newLines.length - 1];
      if (lastLine) {
        tempElement.textContent = lastLine;
        if (tempElement.offsetWidth > containerWidth - 20) {
          const lastWords = lastLine.split(' ');
          let newLastLine = '';
          for (let i = 0; i < lastWords.length; i++) {
            if ((tempElement.textContent ?? '').length > 0) {
              tempElement.textContent += ` ${lastWords[i]}`;
            } else {
              tempElement.textContent = lastWords[i];
            }
            if (tempElement.offsetWidth > containerWidth - 20) {
              newLines[newLines.length - 1] = newLastLine.trim();
              newLines.push(lastWords.slice(i).join(' ').trim());
              break;
            }
            newLastLine += (newLastLine ? ' ' : '') + lastWords[i];
          }
        }
      }

      document.body.removeChild(tempElement);
      setLines(newLines);
    };

    splitTextIntoLines();
    window.addEventListener('resize', splitTextIntoLines);

    return () => window.removeEventListener('resize', splitTextIntoLines);
  }, [code]);

  useEffect(() => {
    if (visibleLines >= lines.length) return;

    const timer = setTimeout(
      () => {
        const container = containerRef.current;
        if (!container) return;

        const nextLineHeight = visibleHeightRef.current + lineHeightRef.current;
        const containerTop = container.getBoundingClientRect().top;

        if (containerTop + nextLineHeight <= window.innerHeight) {
          setVisibleLines((prev) => prev + 1);
          visibleHeightRef.current = nextLineHeight;
        }
      },
      speed * lines[visibleLines].length,
    );

    return () => clearTimeout(timer);
  }, [visibleLines, lines, speed]);

  return (
    <div ref={containerRef} className="hack24-typing-canvas">
      {lines.slice(0, visibleLines).map((line) => (
        <div
          key={line}
          className="hack24-typing-line"
          style={{
            animationDuration: `${speed * line.length}ms`,
            lineHeight: `${lineHeightRef.current}px`,
          }}
        >
          {line}
        </div>
      ))}
    </div>
  );
}
